/**
 * Shared formatting helpers for SharePoint views.
 * Keep all date / size / hash formatting here so all components stay consistent.
 */

/**
 * Format an ISO date string into a human-readable locale string.
 * Returns "—" for null / undefined / unparseable values.
 * @param {string|null} iso
 * @returns {string}
 */
export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Format a byte count into a human-readable string (B / KB / MB / GB).
 * @param {number|null} bytes
 * @returns {string}
 */
export const formatSize = (bytes) => {
  if (bytes == null || bytes === "") return "—";
  const n = Number(bytes);
  if (Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Truncate a content hash to the first N characters for display.
 * @param {string|null} hash
 * @param {number} [length=12]
 * @returns {string}
 */
export const truncateHash = (hash, length = 36) => {
  if (!hash) return "—";
  return hash.length > length ? `${hash.slice(0, length)}…` : hash;
};

/**
 * Return a relative "time ago" string for an ISO timestamp.
 * Gracefully falls back to formatDate for very old timestamps.
 * @param {string|null} iso
 * @returns {string}
 */
export const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(iso);
  } catch {
    return iso;
  }
};
